import type { Group, Permission, UserRole } from '@cheqroom/api';
import { useMemo } from 'react';

export interface Props {
	workspace: Pick<Group, 'profile' | 'limits'> | null;
	user: {
		customer?: string | null;
		userRole?: Pick<UserRole, 'permissions'> | null;
	} | null;
}

const createPermissionHandler = ({ workspace, user }: Props) => {
	const { profile, limits } = workspace ?? {};
	const permissions = user?.userRole?.permissions ?? [];

	const features = {
		useGeo: limits?.allowGeo && profile?.useGeo,
		useImport: limits?.allowImport,
		useExport: limits?.allowExport,
		useCustody: limits?.allowCustody && profile?.useCustody,
		useReservations: limits?.allowReservations && profile?.useReservations,
		useCheckouts: limits?.allowOrders && profile?.useOrders,
		useReleaseAtLocation:
			profile?.custodyCanChangeLocation !== undefined ? profile?.custodyCanChangeLocation : true,
		useKits: limits?.allowKits && profile?.useKits,
		useFlags: limits?.allowFlags && profile?.useFlags,
		useDepreciations: limits?.allowDepreciations && profile?.useDepreciations,
		useSpotchecks: limits?.allowSpotcheck && profile?.useSpotcheck,
		useBlockContacts: limits?.allowBlockContacts && profile?.useBlockContacts,
		useCustomRoles: limits?.allowCustomRoles,
		useTickets: limits?.allowTickets && profile?.useTickets,
		usePdf: limits?.allowGeneratePdf,
		useReservationsClose: limits?.allowReservations && profile?.useReservations && profile?.useReservationsClose,
		useEquipmentPicking: limits?.allowEquipmentPicking && profile?.useEquipmentPicking,
		orderCheckinLocation: limits?.allowOrders && profile?.useOrders && profile?.orderCheckinLocation,
		usePastReservations: limits?.allowReservations && profile?.useReservations && profile?.usePastReservations,
		useBlockEditOfOpenReservations:
			limits?.allowReservations && profile?.useReservations && profile?.useBlockEditOfOpenReservations,
		usePastOrders: limits?.allowOrders && profile?.useOrders && profile?.usePastOrders,
		useBlockEditOfOpenOrders: limits?.allowOrders && profile?.useOrders && profile?.useBlockEditOfOpenOrders,
	};

	const methods = {
		hasPermission: (permission: Permission) => permissions.includes(permission),
		hasAnyPermission: (...required: Permission[]) => required.some((p) => permissions.includes(p)),
		hasAllPermissions: (...required: Permission[]) => required.every((p) => permissions.includes(p)),
		isOwn: (customerId: string) => customerId === user?.customer,

		canUpgrade: () => methods.hasAnyPermission('ACCOUNT_SUBSCRIPTIONS_ADMIN', 'ACCOUNT_BILLING_ADMIN'),
	};

	return {
		...features,
		...methods,
	};
};

export const usePermissions = ({ workspace, user }: Props) => {
	return useMemo(() => createPermissionHandler({ workspace, user }), [workspace, user]);
};
